import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocField,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { splitLines } from "@/lib/tool-kit/essay-templates/text";

/**
 * Porter's five forces: each force is rated 1–5 for how hard it presses on industry
 * profit, with a checklist of the determinants Porter describes, and the ratings are
 * summarised as an overall reading.
 *
 * The reading is a teaching convention, not Porter's formula — Porter did not score the
 * forces, and argued that the strongest force or forces set an industry's profit
 * potential. So the summary reports the strongest forces first and the mean second, and
 * the thresholds below are exported so the page can state them.
 */

export const FORCES = [
  "entrants",
  "suppliers",
  "buyers",
  "substitutes",
  "rivalry",
] as const;

export type Force = (typeof FORCES)[number];

/** 0 means "not rated yet"; 1 is very low pressure on profit, 5 is very high. */
export const RATINGS = [1, 2, 3, 4, 5] as const;

export type Rating = 0 | (typeof RATINGS)[number];

/** A mean at or below this reads as attractive. */
export const ATTRACTIVE_MEAN_MAX = 2;
/** A mean at or above this reads as unattractive. */
export const UNATTRACTIVE_MEAN_MIN = 3.5;
/** A force rated at least this high is listed as one to answer in strategy. */
export const STRONG_RATING_MIN = 4;

export type Reading = "incomplete" | "attractive" | "mixed" | "unattractive";

export interface FiveForcesSummary {
  rated: number;
  /** Mean of the five ratings, or null until all five are rated. */
  mean: number | null;
  /** The force or forces with the highest rating (ties all listed). */
  strongest: Force[];
  /** Forces rated at least `STRONG_RATING_MIN`, in the order of `FORCES`. */
  strong: Force[];
  reading: Reading;
}

export function summarizeForces(
  ratings: Record<Force, Rating>
): FiveForcesSummary {
  const rated = FORCES.filter((force) => ratings[force] > 0);
  const top = Math.max(0, ...FORCES.map((force) => ratings[force]));
  const strongest = top > 0 ? FORCES.filter((f) => ratings[f] === top) : [];
  const strong = FORCES.filter((force) => ratings[force] >= STRONG_RATING_MIN);
  if (rated.length < FORCES.length) {
    return {
      rated: rated.length,
      mean: null,
      strongest,
      strong,
      reading: "incomplete",
    };
  }
  const mean =
    FORCES.reduce((sum, force) => sum + ratings[force], 0) / FORCES.length;
  let reading: Reading = "mixed";
  if (mean <= ATTRACTIVE_MEAN_MAX) {
    reading = "attractive";
  } else if (mean >= UNATTRACTIVE_MEAN_MIN) {
    reading = "unattractive";
  }
  return { rated: rated.length, mean, strongest, strong, reading };
}

export interface ForceInput {
  rating: Rating;
  notes: string;
  /** One flag per determinant of the force, in the order of the label list. */
  drivers: boolean[];
}

export interface FiveForcesInput {
  industry: string;
  forces: Record<Force, ForceInput>;
}

export function ratingsOf(input: FiveForcesInput): Record<Force, Rating> {
  return Object.fromEntries(
    FORCES.map((force) => [force, input.forces[force].rating])
  ) as Record<Force, Rating>;
}

export function emptyForces(
  driverCounts: Record<Force, number>
): Record<Force, ForceInput> {
  return Object.fromEntries(
    FORCES.map((force) => [
      force,
      {
        rating: 0,
        notes: "",
        drivers: Array.from({ length: driverCounts[force] }, () => false),
      },
    ])
  ) as Record<Force, ForceInput>;
}

export interface FiveForcesLabels {
  defaultTitle: string;
  titleWithIndustry: string;
  subtitle: string;
  forceNames: Record<Force, string>;
  /** "Threat of new entrants" is a threat; "Bargaining power of suppliers" is power. */
  drivers: Record<Force, readonly string[]>;
  ratingNames: Record<Exclude<Rating, 0>, string>;
  notRated: string;
  ratingLine: string;
  readingHeading: string;
  readingNote: string;
  readings: Record<Reading, string>;
  fields: {
    industry: string;
    mean: string;
    strongest: string;
    reading: string;
    strong: string;
  };
  noneStrong: string;
  industryPrompt: string;
  tableHead: { force: string; rating: string; drivers: string };
  driversPresent: string;
  driversHeading: string;
  whyPrompts: Record<Force, string>;
  strategyHeading: string;
  strategyNote: string;
  strategySteps: readonly string[];
  listSeparator: string;
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const MEAN_FORMAT: Intl.NumberFormatOptions = {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
};

function ratingText(
  rating: Rating,
  labels: FiveForcesLabels,
  fmt: Format
): string {
  if (rating === 0) {
    return labels.notRated;
  }
  return t(labels.ratingLine, {
    name: labels.ratingNames[rating],
    rating: fmt(rating),
  });
}

function names(forces: readonly Force[], labels: FiveForcesLabels): string {
  return forces
    .map((force) => labels.forceNames[force])
    .join(labels.listSeparator);
}

/** The determinants the writer ticked for `force`, as label text, in list order. */
export function presentDrivers(
  input: FiveForcesInput,
  force: Force,
  labels: Pick<FiveForcesLabels, "drivers">
): string[] {
  return labels.drivers[force].filter(
    (_, index) => input.forces[force].drivers[index] === true
  );
}

function readingBlock(
  input: FiveForcesInput,
  labels: FiveForcesLabels,
  fmt: Format
): DocBlock[] {
  const summary = summarizeForces(ratingsOf(input));
  const fields: DocField[] = [
    { label: labels.fields.reading, value: labels.readings[summary.reading] },
  ];
  if (summary.mean !== null) {
    fields.push({
      label: labels.fields.mean,
      value: fmt(summary.mean, MEAN_FORMAT),
    });
  }
  if (summary.strongest.length > 0) {
    fields.push({
      label: labels.fields.strongest,
      value: names(summary.strongest, labels),
    });
  }
  if (summary.rated === FORCES.length) {
    fields.push({
      label: labels.fields.strong,
      value:
        summary.strong.length > 0
          ? names(summary.strong, labels)
          : labels.noneStrong,
    });
  }
  return [
    { kind: "fields", fields },
    {
      kind: "table",
      head: [
        labels.tableHead.force,
        labels.tableHead.rating,
        labels.tableHead.drivers,
      ],
      rows: FORCES.map((force) => {
        const forceInput = input.forces[force];
        return [
          labels.forceNames[force],
          ratingText(forceInput.rating, labels, fmt),
          t(labels.driversPresent, {
            present: fmt(presentDrivers(input, force, labels).length),
            total: fmt(labels.drivers[force].length),
          }),
        ];
      }),
    },
    { kind: "guidance", text: labels.readingNote },
  ];
}

function forceSection(
  force: Force,
  input: FiveForcesInput,
  labels: FiveForcesLabels,
  fmt: Format
): DocSection {
  const forceInput = input.forces[force];
  const present = presentDrivers(input, force, labels);
  const notes = splitLines(forceInput.notes);
  const blocks: DocBlock[] = [
    {
      kind: "fields",
      fields: [
        {
          label: labels.tableHead.rating,
          value: ratingText(forceInput.rating, labels, fmt),
        },
      ],
    },
  ];
  if (present.length > 0) {
    blocks.push(
      { kind: "subheading", text: labels.driversHeading },
      {
        kind: "list",
        list: { style: "bullet", items: present.map((text) => ({ text })) },
      }
    );
  }
  if (notes.length > 0) {
    blocks.push(
      ...notes.map((text): DocBlock => ({ kind: "paragraph", text }))
    );
  } else {
    blocks.push({ kind: "prompt", text: labels.whyPrompts[force] });
  }
  return { heading: labels.forceNames[force], blocks };
}

export function buildFiveForces(
  input: FiveForcesInput,
  labels: FiveForcesLabels,
  fmt: Format
): DocumentModel {
  const industry = input.industry.trim();
  const sections: DocSection[] = [
    {
      heading: labels.readingHeading,
      blocks: [
        industry === ""
          ? { kind: "prompt", text: labels.industryPrompt }
          : {
              kind: "fields",
              fields: [{ label: labels.fields.industry, value: industry }],
            },
        ...readingBlock(input, labels, fmt),
      ],
    },
    ...FORCES.map((force) => forceSection(force, input, labels, fmt)),
    {
      heading: labels.strategyHeading,
      blocks: [
        { kind: "guidance", text: labels.strategyNote },
        {
          kind: "list",
          list: {
            style: "decimal",
            items: labels.strategySteps.map((text) => ({
              text,
              prompt: true,
            })),
          },
        },
      ],
    },
  ];
  return {
    title: industry
      ? t(labels.titleWithIndustry, { industry })
      : labels.defaultTitle,
    subtitle: labels.subtitle,
    sections,
  };
}
